package br.com.vvrefeicoes.portal.feature.rmp

import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.domain.model.RmpFieldKind
import br.com.vvrefeicoes.portal.domain.model.RmpFormField
import br.com.vvrefeicoes.portal.domain.model.RmpFormSchema
import br.com.vvrefeicoes.portal.domain.model.RmpOptionSource
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlin.math.roundToLong

/** Valor de um campo na tela "Novo RMP", no formato que o usuário vê (data dd/mm/aaaa, moeda 1.234,56). */
sealed interface RmpFieldValue {
    data class Text(
        val value: String,
    ) : RmpFieldValue

    data class Choices(
        val values: Set<String>,
    ) : RmpFieldValue

    data class Period(
        val start: Int?,
        val end: Int?,
    ) : RmpFieldValue
}

internal const val RMP_UNIT_FIELD = "id_empresa"
internal const val RMP_COST_CENTER_FIELD = "id_empresa_centro_custo"

internal val RmpMonths =
    listOf(
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
    )

internal fun RmpFieldValue?.text(): String = (this as? RmpFieldValue.Text)?.value.orEmpty()

/** O campo só aparece (e só é enviado) quando a condição `showWhen` vale. */
internal fun isVisible(
    field: RmpFormField,
    values: Map<String, RmpFieldValue>,
): Boolean = field.showWhen?.let { condition -> values[condition.field].text() == condition.value } ?: true

internal fun isRequired(
    field: RmpFormField,
    values: Map<String, RmpFieldValue>,
): Boolean = field.required || field.requiredWhen?.let { condition -> values[condition.field].text() == condition.value } == true

/** Valores iniciais de um formulário novo (ex.: "Teste prático? Não"). */
internal fun defaultValues(fields: List<RmpFormField>): Map<String, RmpFieldValue> =
    fields
        .filter { it.name != null && it.defaultValue != null }
        .associate { it.name!! to RmpFieldValue.Text(it.defaultValue!!) }

/** Opções (valor → rótulo) de um campo de escolha, fixas ou de unidade. */
internal fun optionsFor(
    field: RmpFormField,
    schema: RmpFormSchema,
): List<Pair<String, String>> =
    when (field.source) {
        RmpOptionSource.LINKED_UNITS -> schema.linkedUnits.map { it.id to it.name }
        RmpOptionSource.ALL_UNITS -> schema.allUnits.map { it.id to it.name }
        RmpOptionSource.UNIT_NAMES -> schema.allUnits.map { it.name to it.name }
        null -> field.options.map { it.value to it.label }
    }

/** Corpo enviado à API: só campos visíveis, preenchidos e editáveis, já no formato da API. */
internal fun valuesToJson(
    fields: List<RmpFormField>,
    values: Map<String, RmpFieldValue>,
): JsonObject =
    buildJsonObject {
        fields.forEach { field ->
            val name = field.name ?: return@forEach
            if (field.kind == RmpFieldKind.SECTION || field.kind.isFile || field.readOnly || !isVisible(field, values)) {
                return@forEach
            }
            val value = values[name]
            // Moeda que aceita zero (gratificação da admissão): em branco vale 0, como no PHP.
            if (field.kind == RmpFieldKind.CURRENCY && field.allowZero && value.text().isBlank()) {
                put(name, JsonPrimitive("0.00"))
                return@forEach
            }
            when (value) {
                is RmpFieldValue.Text -> {
                    val text = value.value.trim()
                    if (text.isNotEmpty()) put(name, JsonPrimitive(textToApi(field.kind, text)))
                }
                is RmpFieldValue.Choices -> {
                    val ordered = field.options.map { it.value }.filter { it in value.values }
                    put(name, JsonArray(ordered.map(::JsonPrimitive)))
                }
                is RmpFieldValue.Period -> {
                    if (value.start != null || value.end != null) {
                        put(
                            name,
                            buildJsonObject {
                                value.start?.let { put("inicio", JsonPrimitive(it)) }
                                value.end?.let { put("fim", JsonPrimitive(it)) }
                            },
                        )
                    }
                }
                null -> Unit
            }
        }
    }

/** Valores de um formulário salvo, no formato da tela. */
internal fun jsonToValues(
    fields: List<RmpFormField>,
    data: JsonObject,
): Map<String, RmpFieldValue> =
    buildMap {
        fields.forEach { field ->
            val name = field.name ?: return@forEach
            val element = data[name] ?: return@forEach
            val value = elementToValue(field.kind, element) ?: return@forEach
            put(name, value)
        }
    }

private fun elementToValue(
    kind: RmpFieldKind,
    element: JsonElement,
): RmpFieldValue? =
    when {
        kind == RmpFieldKind.CHECKBOX && element is JsonArray ->
            RmpFieldValue.Choices(element.mapNotNull { (it as? JsonPrimitive)?.contentOrNull }.toSet())
        kind == RmpFieldKind.MONTH_PERIOD && element is JsonObject ->
            RmpFieldValue.Period(
                start = (element["inicio"] as? JsonPrimitive)?.intOrNull,
                end = (element["fim"] as? JsonPrimitive)?.intOrNull,
            )
        element is JsonPrimitive -> {
            val content = element.contentOrNull
            when {
                content == null -> null
                kind == RmpFieldKind.CURRENCY ->
                    element.jsonPrimitive.doubleOrNull?.let { RmpFieldValue.Text(MoneyFormatter.formatCents((it * 100).roundToLong())) }
                kind == RmpFieldKind.DATE -> RmpFieldValue.Text(isoToDisplayDate(content))
                else -> RmpFieldValue.Text(content)
            }
        }
        else -> null
    }

private fun textToApi(
    kind: RmpFieldKind,
    text: String,
): String =
    when (kind) {
        RmpFieldKind.CURRENCY ->
            MoneyFormatter.parseReaisToCents(text)?.let { cents -> "${cents / 100}.${(cents % 100).toString().padStart(2, '0')}" }
                ?: text
        RmpFieldKind.DATE -> displayToIsoDate(text)
        else -> text
    }

/** "31/12/2026" → "2026-12-31"; texto incompleto vai como está (a API recusa com mensagem). */
internal fun displayToIsoDate(text: String): String {
    val parts = text.split('/')
    return if (parts.size == 3 && parts[0].length == 2 && parts[1].length == 2 && parts[2].length == 4) {
        "${parts[2]}-${parts[1]}-${parts[0]}"
    } else {
        text
    }
}

internal fun isoToDisplayDate(text: String): String {
    val parts = text.take(10).split('-')
    return if (parts.size == 3 && parts[0].length == 4) "${parts[2]}/${parts[1]}/${parts[0]}" else text
}

/** Digita a data só com números: "31122026" → "31/12/2026". */
internal fun maskDate(input: String): String {
    val digits = input.filter(Char::isDigit).take(8)
    return buildString {
        digits.forEachIndexed { index, char ->
            if (index == 2 || index == 4) append('/')
            append(char)
        }
    }
}

/** Digita o valor em centavos: "123456" → "1.234,56". */
internal fun maskCurrency(input: String): String {
    val digits = input.filter(Char::isDigit).trimStart('0').take(13)
    return if (digits.isEmpty()) "" else MoneyFormatter.formatCents(digits.toLong())
}
